import { useTypedRuntimeConfig } from '~/utils/runtimeConfig'

/**
 * Builds a URL for a media asset that carries no transform — logos, DSA badges,
 * the portal status gif. These are served straight from the media host and need
 * no serializer.
 *
 * An asset that *does* carry a transform goes through `serializeAssetUrl` in
 * `~/utils/format/imageUrl` instead.
 *
 * `key` is the **full** object key, folder included
 * (`application_assets/SU_Logo.png`), not a name relative to some folder. No
 * prefix is baked in, because the key space has no single root: the migration
 * mirrored Cloudinary exactly, so `application_assets/...` and bare root keys
 * like `loading_image.png` both exist (ADO 62973). Passing the whole key also
 * means the string at the call site *is* the stored key, which is what makes a
 * mis-cased reference visible in review.
 *
 * The key is used verbatim. R2 keys are case-sensitive byte strings where
 * Cloudinary delivery was not, so a mis-cased key served fine from Cloudinary
 * and 404s here — take the casing from the stored key, never from the
 * Cloudinary URL it replaced (ADO 62986).
 */
export const mediaAssetUrl = (key: string): string => {
  const host = useTypedRuntimeConfig().public.MEDIA_ASSET_URL
  if (!host) {
    throw new Error('MEDIA_ASSET_URL missing in environment')
  }

  return `${host.replace(/\/+$/, '')}/${key}`
}
